<div class="advanced-bundle-edit-container">
  <!-- Tabs Section -->
  <mat-tab-group class="bundle-tabs" data-testid="rb-advbundle-tabs" [selectedIndex]="selectedTabIndex" (selectedTabChange)="onTabChanged($event)">
    <!-- Tab 1: Bundle Information -->
    <mat-tab [label]="bundleInformationTabLabel">
      <div class="tab-content">
        <mat-card class="bundle-info-card">
          <mat-card-content>
            <!-- Bundle Information Grid: 3 columns x 2 rows -->
            <div class="bundle-info-grid" [formGroup]="bundleForm">
              <!-- Row 1, Column 1: Bundle Name -->
              <mat-form-field appearance="outline" class="bundle-field">
                <mat-label>Bundle Name</mat-label>
                <input matInput formControlName="name" placeholder="Enter bundle name" required data-testid="rb-advbundle-name-input" />
                <mat-error *ngIf="bundleForm.controls.name.errors?.required">
                  Bundle name is required.
                </mat-error>
                <mat-error *ngIf="bundleForm.controls.name.errors?.maxlength">
                  Bundle name cannot exceed 100 characters.
                </mat-error>
              </mat-form-field>

              <!-- Row 1, Column 2: Instrument Type (Read-only) -->
              <mat-form-field appearance="outline" class="bundle-field" subscriptSizing="dynamic">
                <mat-label>Instrument Type</mat-label>
                <mat-select [value]="selectedDeviceType" disabled data-testid="rb-advbundle-instrumenttype-select">
                  <mat-option *ngFor="let device of devices" [value]="device.id">
                    {{ device.value }}
                  </mat-option>
                </mat-select>
                <mat-icon matSuffix>lock</mat-icon>
              </mat-form-field>

              <!-- Row 1-2, Column 3: Description (textarea spanning 2 rows) -->
              <mat-form-field appearance="outline" class="bundle-field description-field">
                <mat-label>Description</mat-label>
                <textarea matInput formControlName="description" placeholder="Enter bundle description" rows="4"
                  [style.resize]="'none'" data-testid="rb-advbundle-description-input"></textarea>
                <mat-error *ngIf="bundleForm.controls.description.errors?.maxlength">
                  Description cannot exceed 500 characters.
                </mat-error>
              </mat-form-field>

              <!-- Row 2, Column 1: Date Created (Read-only) -->
              <mat-form-field appearance="outline" class="bundle-field" subscriptSizing="dynamic">
                <mat-label>Date Created</mat-label>
                <input matInput [value]="bundleDateCreated | date: 'shortDate'" readonly disabled data-testid="rb-bundleedit-datecreated-input" />
                <mat-icon matSuffix>lock</mat-icon>
              </mat-form-field>

              <!-- Row 2, Column 2: Created By (Read-only) -->
              <mat-form-field appearance="outline" class="bundle-field" subscriptSizing="dynamic">
                <mat-label>Created By</mat-label>
                <input matInput [value]="bundleCreatedByName" readonly disabled data-testid="rb-bundleedit-createdby-input" />
                <mat-icon matSuffix>lock</mat-icon>
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Bundle Actions -->
        <div class="bundle-actions">
          <button mat-raised-button color="primary" (click)="onSaveBundle()"
            [disabled]="bundleForm.invalid || isLoading" data-testid="rb-advbundle-savechanges-button">
            <mat-icon>save</mat-icon>
            Save Changes
          </button>
          <button mat-stroked-button (click)="onCancel()" [disabled]="isLoading" data-testid="rb-advbundle-cancel-button">
            <mat-icon>cancel</mat-icon>
            Cancel
          </button>
        </div>
      </div>
    </mat-tab>

    <!-- Tab 2: Filters -->
    <mat-tab label="Search for Test Results">
      <div class="tab-content tab-content-with-filters">
        <!-- Filters Section -->
        <mat-card class="filters-card">
          <mat-card-content class="filters-content">
            <!-- Sofia2 Filters Form -->
            <div class="filters-grid" [formGroup]="sofia2FiltersForm" *ngIf="selectedDeviceType === 1">
              <!-- Grid: 6 columns x 3 rows -->
              <!-- Row 1: Test Result ID, Assay ID, Sample ID (textarea), Cassette Lot (textarea), Instrument Serial Number (textarea), Cassette Serial Number (textarea) -->
              <div class="filter-grid">
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Test Result ID</mat-label>
                  <input matInput formControlName="testResultId" placeholder="Enter test result ID" maxlength="500" data-testid="rb-advbundle-sofia2-testresultid-input" />
                  <mat-error *ngIf="sofia2FiltersForm.get('testResultId')?.hasError('maxlength')">
                    Test Result ID cannot exceed 500 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Assay ID</mat-label>
                  <input matInput type="text" formControlName="assayId" placeholder="Enter assay ID" maxlength="9"
                    (keypress)="onAssayIdKeyPress($event)" (input)="onAssayIdInput($event)" data-testid="rb-advbundle-sofia2-assayid-input" />
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('pattern')">
                    Assay ID must be a whole number
                  </mat-error>
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('maxlength')">
                    Assay ID cannot exceed 9 digits
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>Sample ID</mat-label>
                  <textarea matInput formControlName="sampleId"
                    placeholder="Enter sample IDs separated by comma, space, tab, or newline"
                    maxlength="2000" data-testid="rb-advbundle-sofia2-sampleid-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>{{ cassetteLotLabel }}</mat-label>
                  <textarea matInput formControlName="cassetteLot" [placeholder]="cassetteLotPlaceholder" data-testid="rb-advbundle-sofia2-cassettelot-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>Instrument Serial Number</mat-label>
                  <textarea matInput formControlName="instrumentSerialNumber"
                    placeholder="Enter serial numbers separated by comma, space, tab, or newline"
                    maxlength="2000" data-testid="rb-advbundle-sofia2-instrumentserial-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                  <mat-error *ngIf="sofia2FiltersForm.get('instrumentSerialNumber')?.hasError('maxlength')">
                    Instrument Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>{{ cassetteSerialLabel }}</mat-label>
                  <textarea matInput formControlName="cassetteSerialNumber" [placeholder]="cassetteSerialPlaceholder"
                    maxlength="2000" data-testid="rb-advbundle-sofia2-cassetteserial-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                  <mat-error *ngIf="sofia2FiltersForm.get('cassetteSerialNumber')?.hasError('maxlength')">
                    Cassette Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-assay-name">
                  <mat-label>Assay Name</mat-label>
                  <input matInput formControlName="assayName" placeholder="Enter assay name" maxlength="200" data-testid="rb-advbundle-sofia2-assayname-input" />
                  <mat-error *ngIf="sofia2FiltersForm.get('assayName')?.hasError('maxlength')">
                    Assay Name cannot exceed 200 characters
                  </mat-error>
                </mat-form-field>
                <!-- Row 2 -->
                <mat-form-field appearance="outline" class="filter-field filter-row-2" subscriptSizing="dynamic">
                  <mat-label>Start Date</mat-label>
                  <input matInput [matDatepicker]="sofia2StartDatePicker" formControlName="startDate"
                    placeholder="Select start date" data-testid="rb-advbundle-sofia2-startdate-input" />
                  <mat-datepicker-toggle matSuffix [for]="sofia2StartDatePicker"></mat-datepicker-toggle>
                  <mat-datepicker #sofia2StartDatePicker></mat-datepicker>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-row-2" subscriptSizing="dynamic">
                  <mat-label>End Date</mat-label>
                  <input matInput [matDatepicker]="sofia2EndDatePicker" formControlName="endDate"
                    placeholder="Select end date" data-testid="rb-advbundle-sofia2-enddate-input" />
                  <mat-datepicker-toggle matSuffix [for]="sofia2EndDatePicker"></mat-datepicker-toggle>
                  <mat-datepicker #sofia2EndDatePicker></mat-datepicker>
                </mat-form-field>

                <div class="filter-field filter-actions-right filter-row-2">
                  <button mat-raised-button color="primary" (click)="applyFilters()" [disabled]="isLoading" data-testid="rb-advbundle-sofia2-apply-button">
                    <mat-icon>search</mat-icon>
                    Apply
                  </button>
                  <button mat-stroked-button (click)="clearFilters()" [disabled]="isLoading" data-testid="rb-advbundle-sofia2-clear-button">
                    <mat-icon>clear</mat-icon>
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <!-- Savanna Filters Form -->
            <div class="filters-grid" [formGroup]="savannaFiltersForm" *ngIf="selectedDeviceType === 2">
              <!-- Grid: 6 columns x 3 rows -->
              <!-- Row 1: Test Result ID, Assay ID, Sample ID (textarea), Cartridge Lot (textarea), Instrument Serial Number (textarea), Cartridge Serial Number (textarea) -->
              <div class="filter-grid">
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Test Result ID</mat-label>
                  <input matInput formControlName="testResultId" placeholder="Enter test result ID" maxlength="500" data-testid="rb-advbundle-savanna-testresultid-input" />
                  <mat-error *ngIf="savannaFiltersForm.get('testResultId')?.hasError('maxlength')">
                    Test Result ID cannot exceed 500 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Assay ID</mat-label>
                  <input matInput type="text" formControlName="assayId" placeholder="Enter assay ID" maxlength="9"
                    (keypress)="onAssayIdKeyPress($event)" (input)="onAssayIdInput($event)" data-testid="rb-advbundle-savanna-assayid-input" />
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('pattern')">
                    Assay ID must be a whole number
                  </mat-error>
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('maxlength')">
                    Assay ID cannot exceed 9 digits
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>Sample ID</mat-label>
                  <textarea matInput formControlName="sampleId"
                    placeholder="Enter sample IDs separated by comma, space, tab, or newline"
                    maxlength="2000" data-testid="rb-advbundle-savanna-sampleid-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>{{ cassetteLotLabel }}</mat-label>
                  <textarea matInput formControlName="cartridgeLot" [placeholder]="cassetteLotPlaceholder" data-testid="rb-advbundle-savanna-cartridgelot-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>Instrument Serial Number</mat-label>
                  <textarea matInput formControlName="instrumentSerialNumber"
                    placeholder="Enter serial numbers separated by comma, space, tab, or newline"
                    maxlength="2000" data-testid="rb-advbundle-savanna-instrumentserial-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                  <mat-error *ngIf="savannaFiltersForm.get('instrumentSerialNumber')?.hasError('maxlength')">
                    Instrument Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-field-textarea"
                  subscriptSizing="dynamic">
                  <mat-label>{{ cassetteSerialLabel }}</mat-label>
                  <textarea matInput formControlName="cartridgeSerialNumber" [placeholder]="cassetteSerialPlaceholder"
                    maxlength="2000" data-testid="rb-advbundle-savanna-cartridgeserial-input"></textarea>
                  <mat-hint>Enter multiple values separated by comma, space, tab, or newline</mat-hint>
                  <mat-error *ngIf="savannaFiltersForm.get('cartridgeSerialNumber')?.hasError('maxlength')">
                    Cartridge Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-assay-name" subscriptSizing="dynamic">
                  <mat-label>Assay Name</mat-label>
                  <input matInput formControlName="assayName" placeholder="Enter assay name" data-testid="rb-advbundle-savanna-assayname-input" />
                </mat-form-field>
                <!-- Row 2 -->
                <mat-form-field appearance="outline" class="filter-field filter-row-2" subscriptSizing="dynamic">
                  <mat-label>Start Date</mat-label>
                  <input matInput [matDatepicker]="savannaStartDatePicker" formControlName="startDate"
                    placeholder="Select start date" data-testid="rb-advbundle-savanna-startdate-input" />
                  <mat-datepicker-toggle matSuffix [for]="savannaStartDatePicker"></mat-datepicker-toggle>
                  <mat-datepicker #savannaStartDatePicker></mat-datepicker>
                </mat-form-field>

                <mat-form-field appearance="outline" class="filter-field filter-row-2" subscriptSizing="dynamic">
                  <mat-label>End Date</mat-label>
                  <input matInput [matDatepicker]="savannaEndDatePicker" formControlName="endDate"
                    placeholder="Select end date" data-testid="rb-advbundle-savanna-enddate-input" />
                  <mat-datepicker-toggle matSuffix [for]="savannaEndDatePicker"></mat-datepicker-toggle>
                  <mat-datepicker #savannaEndDatePicker></mat-datepicker>
                </mat-form-field>

                <div class="filter-field filter-actions-right filter-row-2">
                  <button mat-raised-button color="primary" (click)="applyFilters()" [disabled]="isLoading" data-testid="rb-advbundle-savanna-apply-button">
                    <mat-icon>search</mat-icon>
                    Apply
                  </button>
                  <button mat-stroked-button (click)="clearFilters()" [disabled]="isLoading" data-testid="rb-advbundle-savanna-clear-button">
                    <mat-icon>clear</mat-icon>
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Results Section -->
        <div class="results-section">
          <div *ngIf="isQueryingResults" class="loading-container">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            <p>Searching for test results...</p>
          </div>

          <!-- Sofia2 Results Table (visible when deviceType is 1) -->
          <div *ngIf="!isQueryingResults && selectedDeviceType === 1" class="device-results-container sofia2-results">
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                  <mat-label>Search test results</mat-label>
                  <input matInput [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange()"
                    placeholder="Type to filter..." data-testid="rb-bundleedit-sofia2-search-input" />
                  <button *ngIf="searchQuery" matSuffix mat-icon-button aria-label="Clear" data-testid="rb-bundleedit-sofia2-clearsearch-button"
                    (click)="searchQuery = ''; onSearchChange()">
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <mat-slide-toggle [checked]="showAlreadyAddedTests" data-testid="rb-bundleedit-sofia2-showalready-toggle"
                  (change)="onToggleShowAlreadyAddedTests($event.checked)" color="primary" class="toggle-filter">
                  Show already added
                </mat-slide-toggle>
                <button mat-raised-button color="primary" (click)="addSelectedTestsToTable()" data-testid="rb-bundleedit-sofia2-addtest-button"
                  [disabled]="isAddTestButtonDisabled()">
                  <mat-icon>add</mat-icon>
                  Add Test<span *ngIf="getSelectedTestsCount() > 0">
                    ({{ getSelectedTestsCount() }})</span>
                </button>
                <button mat-stroked-button type="button" (click)="clearSorting()" aria-label="Clear sorting" data-testid="rb-bundleedit-sofia2-clearsorting-button">
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="table-container mat-elevation-z1">
                <mat-table [dataSource]="sofia2ResultsDataSource" matSort #sofia2ResultsSort="matSort" data-testid="rb-advbundle-sofia2-results-table">
                  <ng-container matColumnDef="select">
                    <mat-header-cell *matHeaderCellDef>
                      <mat-checkbox color="primary" [checked]="areAllCurrentFilteredSelected()"
                        (change)="toggleSelectAllCurrentFiltered()" data-testid="rb-advbundle-sofia2-selectall-checkbox">
                      </mat-checkbox>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <mat-checkbox *ngIf="!isTestAlreadyAdded(result.testResultId)" color="primary"
                        [checked]="isSelected(result.testResultId)" (change)="toggleRowSelection(result.testResultId)"
                        [attr.data-testid]="'rb-advbundle-sofia2-select-checkbox-' + result.testResultId">
                      </mat-checkbox>
                    </mat-cell>
                  </ng-container>

                  <ng-container *ngFor="let column of sofia2Columns" [matColumnDef]="column.key">
                    <mat-header-cell *matHeaderCellDef mat-sort-header [disabled]="!column.sortable">
                      {{ getColumnLabel(column) }}
                      <app-column-filter [tableName]="resultsTableName" [columnName]="column.key"
                        [displayName]="getColumnLabel(column)" [disabled]="isQueryingResults"></app-column-filter>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result" [matTooltip]="
                        column.key === 'dateTimeUtc'
                          ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                          : column.key === 'dateTime'
                            ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                            : column.key === 'result'
                              ? (result.result || '-')
                              : (getColumnValue(result, column.key) || '-')
                      ">
                      <ng-container [ngSwitch]="column.key">
                        <span *ngSwitchCase="'dateTimeUtc'">
                          {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                        </span>
                        <span *ngSwitchCase="'dateTime'">
                          {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                        </span>
                        <span *ngSwitchCase="'result'">
                          <mat-chip *ngIf="result.result && result.result !== '-'" class="result-chip"
                            [class.result-positive]="result.result.toLowerCase() === 'positive'"
                            [class.result-negative]="result.result.toLowerCase() === 'negative'">
                            {{ result.result }}
                          </mat-chip>
                          <span *ngIf="!result.result || result.result === '-'">-</span>
                        </span>
                        <span *ngSwitchDefault>
                          {{ getColumnValue(result, column.key) || '-' }}
                        </span>
                      </ng-container>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="actions">
                    <mat-header-cell *matHeaderCellDef>Action</mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <button mat-icon-button color="primary" size="small" (click)="addSingleTestToTable(result)" data-testid="rb-bundleedit-sofia2-addsingle-button"
                        [disabled]="isTestAlreadyAdded(result.testResultId)" aria-label="Add test">
                        <mat-icon>{{
                          isTestAlreadyAdded(result.testResultId)
                          ? 'check_circle'
                          : 'add_circle'
                          }}</mat-icon>
                      </button>
                      <button *ngIf="isTestAlreadyAdded(result.testResultId)" mat-icon-button color="warn" size="small" data-testid="rb-bundleedit-sofia2-removefromselected-button"
                        (click)="excludeTestFromSelected(result.testResultId)" aria-label="Remove from selected tests">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="noData">
                    <mat-footer-cell *matFooterCellDef [attr.colspan]="sofia2ResultsDisplayedColumns.length">
                      <div class="empty-state">
                        <mat-icon class="empty-icon search">search_off</mat-icon>
                        <h4>No Results Found</h4>
                        <p *ngIf="showResults">Try adjusting your search or filters.</p>
                        <p *ngIf="!showResults">Apply filters above to search for test results.</p>
                        <button *ngIf="showResults" mat-stroked-button color="primary" (click)="clearFilters()" data-testid="rb-bundleedit-sofia2-clearfilters-empty-button">
                          Clear Filters
                        </button>
                      </div>
                    </mat-footer-cell>
                  </ng-container>

                  <mat-header-row *matHeaderRowDef="sofia2ResultsDisplayedColumns; sticky: true"></mat-header-row>
                  <mat-row *matRowDef="let row; columns: sofia2ResultsDisplayedColumns"></mat-row>
                  <mat-footer-row *matFooterRowDef="['noData']"
                    [ngClass]="{ hide: sofia2ResultsDataSource.filteredData.length > 0 }"></mat-footer-row>
                </mat-table>
              </div>

              <mat-paginator #sofia2ResultsPaginator [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
                [showFirstLastButtons]="true" class="mat-elevation-z1" data-testid="rb-advbundle-sofia2-results-paginator"></mat-paginator>
            </div>
          </div>

          <!-- Savanna Results Table (visible when deviceType is 2) -->
          <div *ngIf="!isQueryingResults && selectedDeviceType === 2" class="device-results-container savanna-results">
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                  <mat-label>Search test results</mat-label>
                  <input matInput [(ngModel)]="searchQuery" data-testid="rb-advbundle-savanna-search-input" (ngModelChange)="onSearchChange()"
                    placeholder="Type to filter..." />
                  <button *ngIf="searchQuery" matSuffix mat-icon-button data-testid="rb-advbundle-savanna-search-clear-button" aria-label="Clear"
                    (click)="searchQuery = ''; onSearchChange()">
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <mat-slide-toggle [checked]="showAlreadyAddedTests" data-testid="rb-advbundle-savanna-showalready-toggle"
                  (change)="onToggleShowAlreadyAddedTests($event.checked)" color="primary" class="toggle-filter">
                  Show already added
                </mat-slide-toggle>
                <button mat-raised-button color="primary" data-testid="rb-advbundle-savanna-addtest-button" (click)="addSelectedTestsToTable()"
                  [disabled]="isAddTestButtonDisabled()">
                  <mat-icon>add</mat-icon>
                  Add Test<span *ngIf="getSelectedTestsCount() > 0">
                    ({{ getSelectedTestsCount() }})</span>
                </button>
                <button mat-stroked-button type="button" data-testid="rb-advbundle-savanna-clearsorting-button" (click)="clearSorting()" aria-label="Clear sorting">
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="table-container mat-elevation-z1">
                <mat-table [dataSource]="savannaResultsDataSource" data-testid="rb-advbundle-savanna-results-table" matSort #savannaResultsSort="matSort">
                  <ng-container matColumnDef="select">
                    <mat-header-cell *matHeaderCellDef>
                      <mat-checkbox color="primary" data-testid="rb-advbundle-savanna-selectall-checkbox" [checked]="areAllCurrentFilteredSelected()"
                        (change)="toggleSelectAllCurrentFiltered()">
                      </mat-checkbox>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <mat-checkbox *ngIf="!isTestAlreadyAdded(result.testResultId)" color="primary"
                        [checked]="isSelected(result.testResultId)" [attr.data-testid]="'rb-advbundle-savanna-select-checkbox-' + result.testResultId" (change)="toggleRowSelection(result.testResultId)">
                      </mat-checkbox>
                    </mat-cell>
                  </ng-container>

                  <ng-container *ngFor="let column of savannaColumns" [matColumnDef]="column.key">
                    <mat-header-cell *matHeaderCellDef mat-sort-header [disabled]="!column.sortable">
                      {{ getColumnLabel(column) }}
                      <app-column-filter [tableName]="resultsTableName" [columnName]="column.key"
                        [displayName]="getColumnLabel(column)" [disabled]="isQueryingResults"></app-column-filter>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result" [matTooltip]="
                        column.key === 'dateTimeUtc'
                          ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                          : column.key === 'dateTime'
                            ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                            : column.key === 'result'
                              ? (result.result || '-')
                              : (getColumnValue(result, column.key) || '-')
                      ">
                      <ng-container [ngSwitch]="column.key">
                        <span *ngSwitchCase="'dateTimeUtc'">
                          {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                        </span>
                        <span *ngSwitchCase="'dateTime'">
                          {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                        </span>
                        <span *ngSwitchCase="'result'">
                          <mat-chip *ngIf="result.result && result.result !== '-'" class="result-chip"
                            [class.result-positive]="result.result.toLowerCase() === 'positive'"
                            [class.result-negative]="result.result.toLowerCase() === 'negative'">
                            {{ result.result }}
                          </mat-chip>
                          <span *ngIf="!result.result || result.result === '-'">-</span>
                        </span>
                        <span *ngSwitchDefault>
                          {{ getColumnValue(result, column.key) || '-' }}
                        </span>
                      </ng-container>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="actions">
                    <mat-header-cell *matHeaderCellDef>Action</mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <button mat-icon-button color="primary" size="small" (click)="addSingleTestToTable(result)" data-testid="rb-bundleedit-savanna-addsingle-button"
                        [disabled]="isTestAlreadyAdded(result.testResultId)" aria-label="Add test">
                        <mat-icon>{{
                          isTestAlreadyAdded(result.testResultId)
                          ? 'check_circle'
                          : 'add_circle'
                          }}</mat-icon>
                      </button>
                      <button *ngIf="isTestAlreadyAdded(result.testResultId)" mat-icon-button color="warn" size="small" data-testid="rb-bundleedit-savanna-removefromselected-button"
                        (click)="excludeTestFromSelected(result.testResultId)" aria-label="Remove from selected tests">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="noData">
                    <mat-footer-cell *matFooterCellDef [attr.colspan]="savannaResultsDisplayedColumns.length">
                      <div class="empty-state">
                        <mat-icon class="empty-icon search">search_off</mat-icon>
                        <h4>No Results Found</h4>
                        <p *ngIf="showResults">Try adjusting your search or filters.</p>
                        <p *ngIf="!showResults">Apply filters above to search for test results.</p>
                        <button *ngIf="showResults" mat-stroked-button color="primary" (click)="clearFilters()" data-testid="rb-bundleedit-savanna-clearfilters-empty-button">
                          Clear Filters
                        </button>
                      </div>
                    </mat-footer-cell>
                  </ng-container>

                  <mat-header-row *matHeaderRowDef="savannaResultsDisplayedColumns; sticky: true"></mat-header-row>
                  <mat-row *matRowDef="let row; columns: savannaResultsDisplayedColumns"></mat-row>
                  <mat-footer-row *matFooterRowDef="['noData']"
                    [ngClass]="{ hide: savannaResultsDataSource.filteredData.length > 0 }"></mat-footer-row>
                </mat-table>
              </div>

              <mat-paginator #savannaResultsPaginator [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
                [showFirstLastButtons]="true" class="mat-elevation-z1" data-testid="rb-advbundle-savanna-results-paginator"></mat-paginator>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>

    <!-- Tab 3: Selected Tests -->
    <mat-tab [label]="selectedTestsTabLabel">
      <div class="tab-content">
        <div class="selected-tests-section">
          <!-- Sofia2 Selected Tests Table (visible when deviceType is 1) -->
          <div *ngIf="selectedDeviceType === 1" class="device-results-container sofia2-results">
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                  <mat-label>Search selected tests</mat-label>
                  <input matInput [(ngModel)]="selectedTestsSearchQuery" data-testid="rb-advbundle-sofia2-selectedsearch-input" (ngModelChange)="onSelectedTestsSearchChange()"
                    placeholder="Type to filter..." />
                  <button *ngIf="selectedTestsSearchQuery" matSuffix mat-icon-button data-testid="rb-advbundle-sofia2-selectedsearch-clear-button" aria-label="Clear"
                    (click)="selectedTestsSearchQuery = ''; onSelectedTestsSearchChange()">
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <button mat-raised-button color="warn" data-testid="rb-advbundle-sofia2-deleteselected-button" (click)="deleteSelectedTestsFromSelected()"
                  [disabled]="getSelectedTestsSelectionCount() === 0">
                  <mat-icon>delete</mat-icon>
                  Delete Selected ({{ getSelectedTestsSelectionCount() }})
                </button>
                <button mat-raised-button color="primary" data-testid="rb-advbundle-sofia2-saveselected-button" (click)="onSaveSelectedTests()" [disabled]="isLoading">
                  <mat-icon *ngIf="!isLoading">save</mat-icon>
                  <mat-icon *ngIf="isLoading" class="spinning">refresh</mat-icon>
                  {{
                  isLoading
                  ? 'Saving...'
                  : sofia2SelectedTests.length === 0
                  ? 'Save (Clear All)'
                  : `Save ${sofia2SelectedTests.length} Test Result(s)`
                  }}
                </button>
                <button mat-stroked-button type="button" data-testid="rb-advbundle-sofia2-clearselectedsorting-button" (click)="clearSelectedTestsSorting()"
                  aria-label="Clear sorting">
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="table-container mat-elevation-z1">
                <mat-table [dataSource]="sofia2SelectedTestsDataSource" data-testid="rb-advbundle-sofia2-selected-table" matSort #sofia2SelectedSort="matSort">
                  <ng-container matColumnDef="select">
                    <mat-header-cell *matHeaderCellDef>
                      <mat-checkbox color="primary" data-testid="rb-advbundle-sofia2-selectedselectall-checkbox" [checked]="areAllCurrentSelectedTestsSelected()"
                        (change)="toggleSelectAllSelectedTests()">
                      </mat-checkbox>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <mat-checkbox color="primary" [attr.data-testid]="'rb-advbundle-sofia2-selectedselect-checkbox-' + result.testResultId" [checked]="isSelectedTestSelected(result.testResultId)"
                        (change)="toggleSelectedTestSelection(result.testResultId)">
                      </mat-checkbox>
                    </mat-cell>
                  </ng-container>

                  <ng-container *ngFor="let column of sofia2Columns" [matColumnDef]="column.key">
                    <mat-header-cell *matHeaderCellDef mat-sort-header [disabled]="!column.sortable">
                      {{ getColumnLabel(column) }}
                      <app-column-filter [tableName]="selectedTableName" [columnName]="column.key"
                        [displayName]="getColumnLabel(column)"></app-column-filter>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result" [matTooltip]="
                        column.key === 'dateTimeUtc'
                          ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                          : column.key === 'dateTime'
                            ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                            : column.key === 'result'
                              ? (result.result || '-')
                              : (getColumnValue(result, column.key) || '-')
                      ">
                      <ng-container [ngSwitch]="column.key">
                        <span *ngSwitchCase="'dateTimeUtc'">
                          {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                        </span>
                        <span *ngSwitchCase="'dateTime'">
                          {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                        </span>
                        <span *ngSwitchCase="'result'">
                          <mat-chip *ngIf="result.result && result.result !== '-'" class="result-chip"
                            [class.result-positive]="result.result.toLowerCase() === 'positive'"
                            [class.result-negative]="result.result.toLowerCase() === 'negative'">
                            {{ result.result }}
                          </mat-chip>
                          <span *ngIf="!result.result || result.result === '-'">-</span>
                        </span>
                        <span *ngSwitchDefault>
                          {{ getColumnValue(result, column.key) || '-' }}
                        </span>
                      </ng-container>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="actions">
                    <mat-header-cell *matHeaderCellDef>Action</mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <button mat-icon-button color="warn" size="small" data-testid="rb-bundleedit-sofia2-deletetest-button"
                        (click)="excludeTestFromSelected(result.testResultId)" aria-label="Exclude test">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="noData">
                    <mat-footer-cell *matFooterCellDef [attr.colspan]="sofia2SelectedDisplayedColumns.length"
                      class="empty-state-cell">
                      <div class="empty-state">
                        <mat-icon class="empty-icon">inbox</mat-icon>
                        <h4>No Selected Tests</h4>
                        <p>Use the "Add Test" button in the Filters tab to add tests.</p>
                      </div>
                    </mat-footer-cell>
                  </ng-container>

                  <mat-header-row *matHeaderRowDef="sofia2SelectedDisplayedColumns; sticky: true"></mat-header-row>
                  <mat-row *matRowDef="let row; columns: sofia2SelectedDisplayedColumns"></mat-row>
                  <mat-footer-row *matFooterRowDef="['noData']"
                    [ngClass]="{ hide: sofia2SelectedTestsDataSource.filteredData.length > 0 }"></mat-footer-row>
                </mat-table>
              </div>

              <mat-paginator #sofia2SelectedPaginator [pageSize]="selectedTestsPageSize" data-testid="rb-advbundle-sofia2-selected-paginator"
                [pageSizeOptions]="selectedTestsPageSizeOptions" [showFirstLastButtons]="true"
                class="mat-elevation-z1"></mat-paginator>
            </div>
          </div>

          <!-- Savanna Selected Tests Table (visible when deviceType is 2) -->
          <div *ngIf="selectedDeviceType === 2" class="device-results-container savanna-results">
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                  <mat-label>Search selected tests</mat-label>
                  <input matInput [(ngModel)]="selectedTestsSearchQuery" data-testid="rb-advbundle-savanna-selectedsearch-input" (ngModelChange)="onSelectedTestsSearchChange()"
                    placeholder="Type to filter..." />
                  <button *ngIf="selectedTestsSearchQuery" matSuffix mat-icon-button data-testid="rb-advbundle-savanna-selectedsearch-clear-button" aria-label="Clear"
                    (click)="selectedTestsSearchQuery = ''; onSelectedTestsSearchChange()">
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <button mat-raised-button color="warn" data-testid="rb-advbundle-savanna-deleteselected-button" (click)="deleteSelectedTestsFromSelected()"
                  [disabled]="getSelectedTestsSelectionCount() === 0">
                  <mat-icon>delete</mat-icon>
                  Delete Selected ({{ getSelectedTestsSelectionCount() }})
                </button>
                <button mat-raised-button color="primary" data-testid="rb-advbundle-savanna-saveselected-button" (click)="onSaveSelectedTests()" [disabled]="isLoading">
                  <mat-icon *ngIf="!isLoading">save</mat-icon>
                  <mat-icon *ngIf="isLoading" class="spinning">refresh</mat-icon>
                  {{
                  isLoading
                  ? 'Saving...'
                  : savannaSelectedTests.length === 0
                  ? 'Save (Clear All)'
                  : `Save ${savannaSelectedTests.length} Test Result(s)`
                  }}
                </button>
                <button mat-stroked-button type="button" data-testid="rb-advbundle-savanna-clearselectedsorting-button" (click)="clearSelectedTestsSorting()"
                  aria-label="Clear sorting">
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="table-container mat-elevation-z1">
                <mat-table [dataSource]="savannaSelectedTestsDataSource" data-testid="rb-advbundle-savanna-selected-table" matSort #savannaSelectedSort="matSort">
                  <ng-container matColumnDef="select">
                    <mat-header-cell *matHeaderCellDef>
                      <mat-checkbox color="primary" data-testid="rb-advbundle-savanna-selectedselectall-checkbox" [checked]="areAllCurrentSelectedTestsSelected()"
                        (change)="toggleSelectAllSelectedTests()">
                      </mat-checkbox>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <mat-checkbox color="primary" [attr.data-testid]="'rb-advbundle-savanna-selectedselect-checkbox-' + result.testResultId" [checked]="isSelectedTestSelected(result.testResultId)"
                        (change)="toggleSelectedTestSelection(result.testResultId)">
                      </mat-checkbox>
                    </mat-cell>
                  </ng-container>

                  <ng-container *ngFor="let column of savannaColumns" [matColumnDef]="column.key">
                    <mat-header-cell *matHeaderCellDef mat-sort-header [disabled]="!column.sortable">
                      {{ getColumnLabel(column) }}
                      <app-column-filter [tableName]="selectedTableName" [columnName]="column.key"
                        [displayName]="getColumnLabel(column)"></app-column-filter>
                    </mat-header-cell>
                    <mat-cell *matCellDef="let result" [matTooltip]="
                        column.key === 'dateTimeUtc'
                          ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                          : column.key === 'dateTime'
                            ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                            : column.key === 'result'
                              ? (result.result || '-')
                              : (getColumnValue(result, column.key) || '-')
                      ">
                      <ng-container [ngSwitch]="column.key">
                        <span *ngSwitchCase="'dateTimeUtc'">
                          {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                        </span>
                        <span *ngSwitchCase="'dateTime'">
                          {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                        </span>
                        <span *ngSwitchCase="'result'">
                          <mat-chip *ngIf="result.result && result.result !== '-'" class="result-chip"
                            [class.result-positive]="result.result.toLowerCase() === 'positive'"
                            [class.result-negative]="result.result.toLowerCase() === 'negative'">
                            {{ result.result }}
                          </mat-chip>
                          <span *ngIf="!result.result || result.result === '-'">-</span>
                        </span>
                        <span *ngSwitchDefault>
                          {{ getColumnValue(result, column.key) || '-' }}
                        </span>
                      </ng-container>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="actions">
                    <mat-header-cell *matHeaderCellDef>Action</mat-header-cell>
                    <mat-cell *matCellDef="let result">
                      <button mat-icon-button color="warn" size="small" data-testid="rb-bundleedit-savanna-deletetest-button"
                        (click)="excludeTestFromSelected(result.testResultId)" aria-label="Exclude test">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </mat-cell>
                  </ng-container>

                  <ng-container matColumnDef="noData">
                    <mat-footer-cell *matFooterCellDef [attr.colspan]="savannaSelectedDisplayedColumns.length"
                      class="empty-state-cell">
                      <div class="empty-state">
                        <mat-icon class="empty-icon">inbox</mat-icon>
                        <h4>No Selected Tests</h4>
                        <p>Use the "Add Test" button in the Filters tab to add tests.</p>
                      </div>
                    </mat-footer-cell>
                  </ng-container>

                  <mat-header-row *matHeaderRowDef="savannaSelectedDisplayedColumns; sticky: true"></mat-header-row>
                  <mat-row *matRowDef="let row; columns: savannaSelectedDisplayedColumns"></mat-row>
                  <mat-footer-row *matFooterRowDef="['noData']"
                    [ngClass]="{ hide: savannaSelectedTestsDataSource.filteredData.length > 0 }"></mat-footer-row>
                </mat-table>
              </div>

              <mat-paginator #savannaSelectedPaginator [pageSize]="selectedTestsPageSize"
                [pageSizeOptions]="selectedTestsPageSizeOptions" [showFirstLastButtons]="true"
                class="mat-elevation-z1"></mat-paginator>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>